/**
 * Browser-based mock of the Electron API
 *
 * This provides the same interface as window.electronAPI but uses
 * browser APIs (localStorage, IndexedDB, File API) instead of Node.js.
 *
 * This allows development and testing without Electron.
 */

import type { ElectronAPI } from '@/types';
import { blobManager } from './blobManager';

// Storage keys
const STORAGE_PREFIX = 'emberboard_';
const PROJECTS_KEY = STORAGE_PREFIX + 'projects';

// In-memory store for images (blob URLs)
// Exported so it can be accessed by save/load functions
export const imageStore = new Map<string, string>();

// Event listeners for menu events
const menuEventListeners = new Map<string, Set<(data?: string) => void>>();

/**
 * Get projects storage
 */
function getProjectsStorage(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(PROJECTS_KEY) || '{}');
  } catch {
    return {};
  }
}

/**
 * Save projects storage
 */
function setProjectsStorage(data: Record<string, string>): void {
  localStorage.setItem(PROJECTS_KEY, JSON.stringify(data));
}

/**
 * Browser implementation of the Electron API
 */
export const browserAPI: ElectronAPI = {
  /**
   * Read a file from localStorage
   */
  readFile: async (filePath: string) => {
    try {
      const projects = getProjectsStorage();
      const content = projects[filePath];
      if (content === undefined) {
        return { success: false, error: 'File not found' };
      }
      return { success: true, content };
    } catch (error) {
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * Write a file to localStorage
   */
  writeFile: async (filePath: string, content: string) => {
    try {
      const projects = getProjectsStorage();
      projects[filePath] = content;
      setProjectsStorage(projects);
      return { success: true };
    } catch (error) {
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * Copy an image - in browser, we use blob URLs
   * Returns a blob URL that can be used in img src
   */
  copyImage: async (sourcePath: string, _projectDir: string) => {
    try {
      console.log('[BrowserAPI] copyImage called with:', sourcePath);

      // The sourcePath might already be a blob URL from selectImage
      if (sourcePath.startsWith('blob:')) {
        const fileName = 'image-' + Date.now() + '.png';
        const relativePath = 'images/' + fileName;
        imageStore.set(relativePath, sourcePath);
        console.log('[BrowserAPI] Stored blob URL:', { relativePath, blobUrl: sourcePath });
        console.log('[BrowserAPI] imageStore size:', imageStore.size);
        return {
          success: true,
          relativePath,
          absolutePath: sourcePath,
        };
      }
      // If it's already in our store, just return it
      if (imageStore.has(sourcePath)) {
        return {
          success: true,
          relativePath: sourcePath,
          absolutePath: imageStore.get(sourcePath),
        };
      }
      console.error('[BrowserAPI] Image not found in store:', sourcePath);
      return { success: false, error: 'Image not found' };
    } catch (error) {
      console.error('[BrowserAPI] copyImage error:', error);
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * Check if a file exists
   */
  fileExists: async (filePath: string) => {
    const projects = getProjectsStorage();
    return filePath in projects || imageStore.has(filePath);
  },

  /**
   * Open a file picker for images
   */
  selectImage: async () => {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';

      input.onchange = () => {
        const file = input.files?.[0];
        if (!file) {
          resolve({ success: false, canceled: true });
          return;
        }

        // Create a blob URL for the selected image
        const blobUrl = URL.createObjectURL(file);
        resolve({
          success: true,
          filePath: blobUrl,
        });
      };

      input.oncancel = () => {
        resolve({ success: false, canceled: true });
      };

      // Trigger file picker
      input.click();
    });
  },

  /**
   * Show a save dialog - in browser, just prompt for filename
   */
  selectProjectDir: async () => {
    const fileName = prompt('Enter project name:', 'my-project');
    if (!fileName) {
      return { success: false, canceled: true };
    }
    return {
      success: true,
      filePath: fileName + '.emberboard',
    };
  },

  /**
   * Register a menu event listener
   */
  onMenuEvent: (event, callback) => {
    if (!menuEventListeners.has(event)) {
      menuEventListeners.set(event, new Set());
    }
    menuEventListeners.get(event)!.add(callback);

    // Return cleanup function
    return () => {
      menuEventListeners.get(event)?.delete(callback);
    };
  },
};

/**
 * Trigger a menu event (for testing or keyboard shortcuts)
 */
export function triggerMenuEvent(event: string, data?: string): void {
  const listeners = menuEventListeners.get(event);
  if (listeners) {
    listeners.forEach(callback => callback(data));
  }
}

/**
 * Get blob URL for an image path
 */
export function getImageUrl(relativePath: string | null): string | null {
  if (!relativePath) return null;

  console.log('[BrowserAPI] getImageUrl called with:', relativePath);
  console.log('[BrowserAPI] imageStore contents:', Array.from(imageStore.entries()));

  // Check if it's already a blob URL
  if (relativePath.startsWith('blob:')) {
    console.log('[BrowserAPI] Path is already a blob URL');
    return relativePath;
  }

  // Check our image store (for beat images)
  const url = imageStore.get(relativePath);
  if (url) {
    console.log('[BrowserAPI] Retrieved from imageStore:', url);
    return url;
  }

  // Check blobManager (for reference images)
  const blobUrl = blobManager.get(relativePath);
  if (blobUrl) {
    console.log('[BrowserAPI] Retrieved from blobManager:', blobUrl);
    return blobUrl;
  }

  console.log('[BrowserAPI] Image not found in any store');
  return null;
}

/**
 * Install the browser API on window
 */
export function installBrowserAPI(): void {
  if (!window.electronAPI) {
    window.electronAPI = browserAPI;
    console.log('[EmberBoard] Browser API installed (Electron not detected)');
  }
}
